import { AppIcon } from '@/components/ui/app-icon';
import { Logo } from '@/components/logo.component';
import { Button } from '@/components/ui/button';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import { iconSpinnerThird } from '@/lib/icons';
import { BRAND } from '@/lib/brand';

type IconShim = React.ComponentType<{ className?: string }>;
const Loader2: IconShim = (p) => <AppIcon icon={iconSpinnerThird} {...p} />;

const DEFAULT_LOOPBACK_PORT = 56789;

/** Parse a search-param port and return it only when it's a valid TCP port. */
function parseLoopbackPort(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 65535) {
    return undefined;
  }
  return parsed;
}

const asString = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : undefined;

export const Route = createFileRoute('/mcp-auth-callback')({
  component: MCPAuthCallback,
  validateSearch: (search: Record<string, unknown>) => ({
    code: asString(search.code),
    state: asString(search.state),
    error: asString(search.error),
    error_description: asString(search.error_description),
    port:
      typeof search.port === 'string'
        ? parseLoopbackPort(search.port)
        : undefined,
  }),
});

function MCPAuthCallback() {
  const { code, state, error, error_description, port } = Route.useSearch();
  const [forwarded, setForwarded] = useState(false);

  const loopbackUrl = useMemo(() => {
    if (!code || !state) return null;
    const p = port ?? DEFAULT_LOOPBACK_PORT;
    return (
      `http://127.0.0.1:${p}/callback?` +
      new URLSearchParams({
        code,
        state,
      }).toString()
    );
  }, [code, state, port]);

  useEffect(() => {
    if (loopbackUrl && !forwarded) {
      setForwarded(true);
      window.location.href = loopbackUrl;
    }
  }, [loopbackUrl, forwarded]);

  return (
    <div className='flex min-h-screen items-center justify-center bg-linear-to-br from-slate-50 via-white to-slate-50 p-4 dark:bg-black'>
      <div className='w-full max-w-md text-center'>
        <Logo className='mx-auto mb-6 h-8 w-auto' />
        <div className='rounded-lg border border-slate-200 bg-white p-8 shadow-xl dark:border-slate-800 dark:bg-slate-900'>
          {error ? (
            <>
              <h2 className='mb-2 text-lg font-semibold text-foreground'>
                Sign-in failed
              </h2>
              <p className='text-sm text-muted-foreground'>
                <span className='font-mono'>{error}</span>
                {error_description ? `: ${error_description}` : null}
              </p>
              <p className='mt-4 text-xs text-muted-foreground'>
                Close this tab and rerun{' '}
                <code className='font-mono'>cleanlist-mcp-login</code>.
              </p>
            </>
          ) : !code || !state ? (
            <>
              <h2 className='mb-2 text-lg font-semibold text-foreground'>
                Missing authorization code
              </h2>
              <p className='text-sm text-muted-foreground'>
                Clerk redirected here without an OAuth code or state. The
                sign-in flow may have been interrupted — please rerun{' '}
                <code className='font-mono'>cleanlist-mcp-login</code>.
              </p>
            </>
          ) : (
            <>
              <Loader2 className='mx-auto mb-4 h-8 w-8 animate-spin text-[#3E8BFF]' />
              <h2 className='mb-2 text-lg font-semibold text-foreground'>
                Completing {BRAND.name} MCP sign-in…
              </h2>
              <p className='text-sm text-muted-foreground'>
                Forwarding to the local CLI on port{' '}
                {port ?? DEFAULT_LOOPBACK_PORT}.
              </p>
              {forwarded && loopbackUrl && (
                <Button
                  variant='outline'
                  size='sm'
                  className='mt-4'
                  onClick={() => {
                    window.location.href = loopbackUrl;
                  }}
                >
                  Click here if it doesn't redirect automatically
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MCPAuthCallback;
